// Public document upload portal — shown to clients via /upload/:token.
// No auth: the token in the URL is the only credential.
//
// The client uploads each REQUESTED document into its own slot (Lissette
// 2026-06-22: "que al darle click suba lo que le piden, y así el siguiente").
// Each file is renamed with the document's label before upload so the agent
// sees exactly which requested document it is, and the checklist marks the
// matching item done. Files still go straight to Azure Blob via SAS; metadata
// is recorded so the agent sees them in the CRM. When the request lists no
// specific documents, a generic uploader is shown instead.

import { useEffect, useState } from 'react';
import { Upload, Camera, FileCheck, Shield, AlertCircle } from 'lucide-react';
import { viewDocPortalPublic, uploadToDocPortalPublic } from '../api.js';
import { toast } from './Toast.jsx';
import { CameraCapture, isMobileDevice } from './CameraCapture.jsx';
import { idiomaDelPortal, idiomaDelEnlace } from '../idiomaDelPortal.js';

// NOTE: `.heic` is deliberately NOT here. iOS Safari transcodes an iPhone
// photo to JPEG on `<input type=file>` ONLY when the accept list doesn't
// allow HEIC — list `.heic` and it hands over the original. Windows can't
// open HEIC without a Store codec and no desktop browser can render it, so
// agents ended up with documents they couldn't see (Daniel Velez 2026-08-03).
// Dropping it from the list makes the phone do the conversion for us.
const ACCEPT = '.pdf,image/*,.jpg,.jpeg,.png,.webp';

function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  // en-US siempre, tambien en espanol: es la convencion de la casa para las
  // fechas, y el cliente esta en Estados Unidos.
  return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Mirror the backend's filename sanitizer (documents.routes.ts) so the
// per-document "received?" check compares against the name the server will
// actually store.
const sanitizeName = (s) => String(s || '').replace(/[^a-zA-Z0-9._\- ()]/g, '_');
// Prefix a requested-document label puts in front of every file uploaded for
// it, e.g. "Prueba de Ingresos - ". The " - " survives sanitization, so the
// agent and the checklist can both tell which file fulfills which document.
const labelPrefix = (label) => `${sanitizeName(label)} - `;

// Re-encode image files through a canvas to drop EXIF metadata (GPS coords,
// device serial, timestamps). The portal accepts photos taken with phone
// cameras, and modern iPhones/Androids embed GPS + device info in JPEGs by
// default — leaking the patient's home location into Azure Blob would be a
// HIPAA Minimum Necessary violation.
//
// PDFs and non-image files pass through unchanged.
//
// The re-encode also normalizes the format: HEIC (the odd Mac/iPad upload that
// slips past ACCEPT) comes out as JPEG, and anything else non-JPEG comes out as
// PNG. `createImageBitmap` can't decode HEIC outside Safari, so those fall back
// to the original bytes untouched — better a file the agent must convert than a
// failed upload.
//
// The stored filename MUST carry the extension of what we actually re-encoded:
// the backend cross-checks extension vs magic bytes and rejects a mismatch, so
// e.g. a .webp re-encoded to PNG has to be renamed .png or the upload 400s.
async function stripExifIfImage(file) {
  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0);
    if (bitmap.close) bitmap.close();
    const asJpeg = /jpe?g|heic|heif/i.test(file.type);
    const outType = asJpeg ? 'image/jpeg' : 'image/png';
    const quality = asJpeg ? 0.92 : undefined;
    const blob = await new Promise((res) => canvas.toBlob(res, outType, quality));
    if (!blob) return file;
    const base = String(file.name || 'foto').replace(/\.[a-z0-9]+$/i, '') || 'foto';
    const outName = `${base}.${asJpeg ? 'jpg' : 'png'}`;
    return new File([blob], outName, { type: outType, lastModified: Date.now() });
  } catch {
    return file;
  }
}

export function DocPortalLandingPage({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  // Desktop browsers ignore <input capture>, so on desktop we open a webcam
  // modal instead. On mobile we keep the native input which opens the OS
  // camera UI directly. cameraLabel remembers which requested document the
  // webcam shot should be filed under.
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraLabel, setCameraLabel] = useState(null);
  const isMobile = isMobileDevice();

  // El idioma con el que se le habla al cliente. Arranca con lo que se sepa sin
  // preguntar al servidor —el `?lang=` del enlace o el navegador— y se corrige
  // en cuanto llega su ficha. El boton ES/EN de la cabecera manda sobre todo:
  // desde que el cliente lo toca, la pagina no le vuelve a cambiar el idioma.
  const [lang, setLang] = useState(() => idiomaDelPortal({
    delEnlace: typeof window === 'undefined' ? null : idiomaDelEnlace(window.location.search),
    delNavegador: typeof navigator === 'undefined' ? null : navigator.language,
  }));
  const [idiomaElegido, setIdiomaElegido] = useState(false);
  const es = lang === 'es';
  const t = (textoEs, textoEn) => (es ? textoEs : textoEn);

  const refresh = () => viewDocPortalPublic(token).then(setData).catch((e) => setErr(e.message));

  useEffect(() => {
    setLoading(true);
    viewDocPortalPublic(token)
      .then((d) => {
        setData(d);
        // El idioma de SU ficha, que es lo que la agencia sabe de esta persona
        // y gana al idioma del telefono. No se toca si el enlace lo forzo o si
        // el cliente ya eligio con el boton.
        if (!idiomaElegido && !idiomaDelEnlace(window.location.search) && d?.clientLanguage) {
          setLang(idiomaDelPortal({ deLaFicha: d.clientLanguage }));
        }
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Upload one or more files. When `label` is set, each file is renamed with
  // the requested document's prefix so it lands in the right slot.
  const handleFiles = async (fileList, label = null) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    setProgress({ done: 0, total: files.length, currentName: '' });
    let done = 0;
    let failedCount = 0;
    for (const file of files) {
      setProgress({ done, total: files.length, currentName: file.name });
      if (file.size > 25 * 1024 * 1024) {
        toast.warn(t(`${file.name} pesa más de 25 MB — no se subió`, `${file.name} is over 25 MB — skipped`));
        failedCount++;
        continue;
      }
      try {
        const cleaned = await stripExifIfImage(file);
        const named = label
          ? new File([cleaned], `${label} - ${cleaned.name}`, { type: cleaned.type, lastModified: cleaned.lastModified })
          : cleaned;
        await uploadToDocPortalPublic(token, named);
        done++;
      } catch (e) {
        console.error(e);
        toast.error(t(`No se pudo subir ${file.name}: ${e.message}`, `Failed to upload ${file.name}: ${e.message}`));
        failedCount++;
      }
    }
    setProgress({ done, total: files.length, currentName: '' });
    setUploading(false);
    await refresh();
    if (failedCount === 0) setProgress(null);
  };

  const openCameraFor = (label) => { setCameraLabel(label || null); setCameraOpen(true); };

  if (loading) return <CenteredMessage>{t('Cargando…', 'Loading…')}</CenteredMessage>;
  if (err || !data) return (
    <CenteredMessage>
      <h2 style={{ margin: '0 0 .5rem', color: '#ff3d6b' }}>{t('Enlace no válido', 'Link not valid')}</h2>
      <p>{t('Este enlace pudo haber vencido o haber sido eliminado. Comunícate con tu agente.', 'This upload link may have expired or been removed. Please contact your agent.')}</p>
    </CenteredMessage>
  );

  const expired = data.expiresAt && new Date(data.expiresAt) < new Date();
  const completed = data.status === 'completed';
  const documentsNeeded = data.documentsNeeded || [];
  const requestedCount = documentsNeeded.length;
  const uploads = data.uploads || [];
  const uploadCount = uploads.length;
  // A requested document is "received" when some upload's stored name starts
  // with its label prefix.
  const isDocSatisfied = (label) => {
    const pre = labelPrefix(label).toLowerCase();
    return uploads.some((u) => sanitizeName(u.name).toLowerCase().startsWith(pre));
  };
  const satisfiedCount = documentsNeeded.filter(isDocSatisfied).length;

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg,#0a1628 0%,#0d1e36 100%)', color: '#e6f0fa', padding: '2rem 1rem' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* El cliente siempre tiene la ultima palabra sobre lo que lee: la ficha
            puede venir con el idioma por defecto (ingles) sin que nadie lo haya
            elegido, y desde aqui lo corrige en un toque. */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '.75rem' }}>
          <button type="button" onClick={() => { setIdiomaElegido(true); setLang(es ? 'en' : 'es'); }}
            title={t('Ver esta página en inglés', 'Ver esta página en español')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '.35rem .7rem', borderRadius: 999, background: 'rgba(0,212,255,.1)', border: '1px solid rgba(0,212,255,.35)', color: '#00d4ff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            {es ? '🇺🇸 English' : '🇪🇸 Español'}
          </button>
        </div>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          {data.agencyLogoUrl && (
            <div style={{ marginBottom: '.9rem', textAlign: 'center' }}>
              <img src={data.agencyLogoUrl} alt={data.agencyName || ''} style={{ display: 'block', margin: '0 auto', maxHeight: 72, maxWidth: 240, objectFit: 'contain' }} />
            </div>
          )}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '.4rem 1rem', borderRadius: 999, background: 'rgba(0,212,255,.1)', color: '#00d4ff', fontSize: 12, fontWeight: 700, letterSpacing: '.5px' }}>
            <Shield size={14} /> {data.agencyName || t('SUBIR DOCUMENTOS', 'DOCUMENT UPLOAD')}
          </div>
          <h1 style={{ fontSize: '1.8rem', margin: '1rem 0 .3rem' }}>{t('Hola', 'Hi')} {data.clientName},</h1>
          <p style={{ color: '#7aa3c0', margin: 0 }}>{data.title}</p>
          {data.description && <p style={{ color: '#7aa3c0', fontSize: 13, marginTop: 6 }}>{data.description}</p>}
          {data.expiresAt && (
            <p style={{ color: '#45688a', fontSize: 12, marginTop: 6 }}>
              {t('Enlace válido hasta el', 'Link valid until')} {fmtDate(data.expiresAt)}
            </p>
          )}
        </div>

        {expired && (
          <div style={{ background: 'rgba(255,61,107,.1)', border: '1px solid #ff3d6b', borderRadius: 12, padding: '1rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '.6rem' }}>
            <AlertCircle size={18} style={{ color: '#ff3d6b' }} />
            <div><strong style={{ color: '#ff3d6b' }}>{t('Este enlace venció.', 'This link has expired.')}</strong> {t('Comunícate con tu agente para pedir uno nuevo.', 'Contact your agent to request a new one.')}</div>
          </div>
        )}

        {!expired && (
          <>
            {requestedCount > 0 ? (
              <>
                {/* Per-document upload: each requested item has its own slot. */}
                <div style={{ fontSize: 12, color: '#7aa3c0', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '.6rem' }}>
                  {t('Documentos solicitados', 'Documents requested')} ({satisfiedCount}/{requestedCount})
                </div>
                {documentsNeeded.map((label, i) => (
                  <DocRow
                    key={i}
                    label={label}
                    done={isDocSatisfied(label)}
                    disabled={uploading}
                    isMobile={isMobile}
                    onFiles={(files) => handleFiles(files, label)}
                    onCamera={() => openCameraFor(label)}
                    es={es}
                  />
                ))}
                {/* Anything not on the list. */}
                <DocRow
                  label={t('Otro documento', 'Other document')}
                  extra
                  done={false}
                  disabled={uploading}
                  isMobile={isMobile}
                  onFiles={(files) => handleFiles(files, null)}
                  onCamera={() => openCameraFor(null)}
                  es={es}
                />
              </>
            ) : (
              // No specific documents requested — generic uploader.
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '.8rem', marginBottom: '1.5rem' }}>
                <UploadCard
                  label={t('Elegir del dispositivo', 'Pick from device')}
                  sublabel={t('Escoge archivos (PDF, imagen)', 'Choose files (PDF, image)')}
                  icon={<Upload size={28} />}
                  disabled={uploading}
                  inputProps={{ accept: ACCEPT, multiple: true }}
                  onFiles={(files) => handleFiles(files, null)}
                />
                {isMobile ? (
                  <UploadCard
                    label={t('Tomar una foto', 'Take a photo')}
                    sublabel={t('Usa la cámara de tu teléfono', 'Use your phone camera')}
                    icon={<Camera size={28} />}
                    disabled={uploading}
                    inputProps={{ accept: 'image/*', capture: 'environment' }}
                    onFiles={(files) => handleFiles(files, null)}
                  />
                ) : (
                  <UploadCardButton
                    label={t('Tomar una foto', 'Take a photo')}
                    sublabel={t('Usa la cámara de tu computadora', 'Use your webcam')}
                    icon={<Camera size={28} />}
                    disabled={uploading}
                    onClick={() => openCameraFor(null)}
                  />
                )}
              </div>
            )}

            {progress && (
              <div style={{ background: 'rgba(0,212,255,.08)', border: '1px solid #00d4ff', borderRadius: 10, padding: '.8rem 1rem', margin: '1rem 0' }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>
                  {uploading
                    ? t(`Subiendo ${progress.done + 1} de ${progress.total}…`, `Uploading ${progress.done + 1}/${progress.total}…`)
                    : t(`Se subieron ${progress.done} de ${progress.total} archivos`, `Uploaded ${progress.done}/${progress.total} files`)}
                </div>
                {progress.currentName && <div style={{ fontSize: 12, color: '#7aa3c0', marginTop: 2 }}>{progress.currentName}</div>}
              </div>
            )}

            {uploadCount > 0 && (
              <div style={{ background: 'rgba(0,255,136,.05)', border: '1px solid rgba(0,255,136,.2)', borderRadius: 12, padding: '1rem 1.2rem', margin: '1.5rem 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '.5rem' }}>
                  <FileCheck size={16} style={{ color: '#00ff88' }} />
                  <strong style={{ color: '#00ff88' }}>{t('Archivos recibidos', 'Files received')} ({uploadCount})</strong>
                </div>
                <ul style={{ margin: 0, paddingLeft: 20, color: '#7aa3c0', fontSize: 13 }}>
                  {uploads.map((u) => (
                    <li key={u.id} style={{ padding: '.15rem 0' }}>{u.name}</li>
                  ))}
                </ul>
              </div>
            )}

            {completed && (
              <div style={{ textAlign: 'center', padding: '1.5rem', background: 'rgba(0,255,136,.1)', border: '1px solid #00ff88', borderRadius: 12, marginBottom: '1.5rem' }}>
                <FileCheck size={32} style={{ color: '#00ff88' }} />
                <div style={{ fontWeight: 700, color: '#00ff88', marginTop: '.5rem' }}>{t('Recibimos todos los archivos. ¡Gracias!', 'All files received — thank you!')}</div>
              </div>
            )}
          </>
        )}

        <div style={{ textAlign: 'center', color: '#45688a', fontSize: 11, marginTop: '2rem' }}>
          Powered by Deskte · Insurance Agent CRM
        </div>
      </div>

      <CameraCapture
        open={cameraOpen}
        isEs={es}
        onClose={() => setCameraOpen(false)}
        onCapture={(file) => {
          setCameraOpen(false);
          handleFiles([file], cameraLabel);
        }}
      />
    </div>
  );
}

// One requested document = one row with its own Upload + Photo controls.
// `extra` marks the catch-all "Other document" row (no checkmark state).
function DocRow({ label, done, disabled, isMobile, onFiles, onCamera, extra = false, es = false }) {
  const t = (textoEs, textoEn) => (es ? textoEs : textoEn);
  const [dragOver, setDragOver] = useState(false);
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  const smallBtn = {
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '.45rem .7rem', borderRadius: 9,
    background: 'rgba(0,212,255,.1)', border: '1px solid rgba(0,212,255,.4)', color: '#00d4ff',
    fontSize: 12, fontWeight: 700, cursor: disabled ? 'wait' : 'pointer', opacity: disabled ? 0.6 : 1, whiteSpace: 'nowrap',
  };
  return (
    <div
      onDragEnter={(e) => { stop(e); if (!disabled) setDragOver(true); }}
      onDragOver={(e) => { stop(e); if (!disabled) setDragOver(true); }}
      onDragLeave={(e) => { stop(e); setDragOver(false); }}
      onDrop={(e) => { stop(e); setDragOver(false); if (!disabled && e.dataTransfer.files?.length) onFiles(e.dataTransfer.files); }}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
        padding: '.7rem .9rem', borderRadius: 12, marginBottom: '.6rem',
        background: dragOver ? 'rgba(0,212,255,.16)' : done ? 'rgba(0,255,136,.06)' : extra ? 'rgba(255,255,255,.03)' : 'rgba(255,255,255,.04)',
        border: `1px ${extra ? 'dashed' : 'solid'} ${done ? 'rgba(0,255,136,.35)' : extra ? 'rgba(122,163,192,.3)' : 'rgba(122,163,192,.2)'}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
        <span style={{ fontSize: 18 }}>{done ? '✅' : extra ? '➕' : '📄'}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
          <div style={{ fontSize: 12, color: done ? '#00ff88' : '#7aa3c0' }}>{done ? t('Recibido', 'Received') : extra ? t('Opcional', 'Optional') : t('Pendiente', 'Pending')}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <label style={smallBtn}>
          <Upload size={14} /> {done ? t('Reemplazar', 'Replace') : t('Subir', 'Upload')}
          <input type="file" accept={ACCEPT} multiple onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }} disabled={disabled} style={{ display: 'none' }} />
        </label>
        {isMobile ? (
          <label style={smallBtn} title={t('Tomar una foto', 'Take a photo')}>
            <Camera size={14} />
            <input type="file" accept="image/*" capture="environment" onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }} disabled={disabled} style={{ display: 'none' }} />
          </label>
        ) : (
          <button type="button" onClick={onCamera} disabled={disabled} style={smallBtn} title={t('Tomar una foto', 'Take a photo')}>
            <Camera size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

// Same look as UploadCard but a real button (no hidden file input).
// Used for the desktop "Take a photo" path that opens the webcam modal.
function UploadCardButton({ label, sublabel, icon, disabled, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.4rem',
        padding: '1.5rem 1rem', background: 'rgba(0,212,255,.06)', border: '2px dashed #00d4ff',
        borderRadius: 14, cursor: disabled ? 'wait' : 'pointer', opacity: disabled ? 0.6 : 1,
        transition: 'all .15s ease', color: '#e6f0fa', textAlign: 'center',
      }}
    >
      <div style={{ color: '#00d4ff' }}>{icon}</div>
      <div style={{ fontWeight: 700, fontSize: 14 }}>{label}</div>
      <div style={{ fontSize: 12, color: '#7aa3c0' }}>{sublabel}</div>
    </button>
  );
}

function UploadCard({ label, sublabel, icon, disabled, inputProps, onFiles }) {
  const [dragOver, setDragOver] = useState(false);
  // The dashed border makes this look like a drop zone, so support it for
  // real: without onDrop the browser just opens the dropped file in a new
  // tab. preventDefault+stopPropagation on all four events (see
  // feedback_drag_drop_needs_handlers). Drops are ignored while disabled.
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  return (
    <label
      onDragEnter={(e) => { stop(e); if (!disabled) setDragOver(true); }}
      onDragOver={(e) => { stop(e); if (!disabled) setDragOver(true); }}
      onDragLeave={(e) => { stop(e); setDragOver(false); }}
      onDrop={(e) => { stop(e); setDragOver(false); if (!disabled && e.dataTransfer.files?.length) onFiles(e.dataTransfer.files); }}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.4rem',
        padding: '1.5rem 1rem', background: dragOver ? 'rgba(0,212,255,.16)' : 'rgba(0,212,255,.06)',
        border: '2px dashed #00d4ff',
        borderRadius: 14, cursor: disabled ? 'wait' : 'pointer', opacity: disabled ? 0.6 : 1,
        transition: 'all .15s ease',
    }}>
      <div style={{ color: '#00d4ff' }}>{icon}</div>
      <div style={{ fontWeight: 700, fontSize: 14 }}>{label}</div>
      <div style={{ fontSize: 12, color: '#7aa3c0' }}>{sublabel}</div>
      <input
        type="file"
        {...inputProps}
        onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }}
        disabled={disabled}
        style={{ display: 'none' }}
      />
    </label>
  );
}

function CenteredMessage({ children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a1628', color: '#e6f0fa', padding: '2rem' }}>
      <div style={{ textAlign: 'center', maxWidth: 480 }}>{children}</div>
    </div>
  );
}
